import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Mensajes de estado que se ANUNCIAN sin mover el foco (WCAG 4.1.3).
 *
 * El problema que resuelve: cuando algo sale bien —se guardó una cuenta, se
 * repartió una contraseña— la pantalla cambia en un rincón y quien usa lector
 * de pantalla no se entera de nada. Un `role="status"` con `aria-live` lo
 * dice; mover el foco al mensaje lo diría también pero arrancaría a la
 * persona de donde estaba trabajando, que es peor.
 *
 * `polite` y no `assertive`: esto confirma, no interrumpe. Lo que interrumpe
 * es un error, y los errores viven junto a su campo con `role="alert"`.
 */

export interface Mensaje {
  id: number;
  texto: string;
}

export function useAvisos() {
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const siguiente = useRef(1);

  const avisar = useCallback((texto: string) => {
    const id = siguiente.current++;
    setMensajes((m) => [...m, { id, texto }]);
    // Se va solo a los 6 s: suficiente para leerlo sin prisa y poco para que
    // se acumulen. No se puede cerrar a mano a propósito — un botón de cerrar
    // en un aviso que ya se va es un objetivo táctil que estorba.
    window.setTimeout(() => {
      setMensajes((m) => m.filter((x) => x.id !== id));
    }, 6000);
  }, []);

  return { mensajes, avisar };
}

export function Avisos({ mensajes }: { mensajes: Mensaje[] }) {
  // El contenedor existe SIEMPRE, aunque esté vacío: una región viva que se
  // crea en el momento de tener algo que decir no se anuncia, porque el
  // lector tiene que estar observándola de antes. Es el error clásico de
  // `aria-live` y no deja rastro: simplemente nadie oye nada.
  return (
    <div className="avisos" role="status" aria-live="polite" aria-atomic="false">
      {mensajes.map((m) => (
        <div key={m.id}>{m.texto}</div>
      ))}
    </div>
  );
}

/** Para pantallas que quieran anunciar al montarse (no se usa hoy; existe
 *  para que el patrón quede escrito en un solo lugar). */
export function useAnuncioAlMontar(avisar: (t: string) => void, texto: string | null) {
  useEffect(() => {
    if (texto) avisar(texto);
  }, [avisar, texto]);
}
